import type { SkillGapSectionGroup, SkillGapSummary } from '../../../lib/optimization/skillGapRecommendations'
import {
  SECTION_FEEDBACK_CALLOUT_CLASS,
  SECTION_FEEDBACK_LIST_CLASS,
  SECTION_FEEDBACK_TITLE_CLASS,
} from './sectionFeedbackCallout'

interface OptimizationSkillsGapPanelProps {
  summary: SkillGapSummary | null
  /** Compact layout for Tailoring results modal */
  variant?: 'section' | 'modal'
}

const TONE_CHIP_CLASS: Record<SkillGapSectionGroup['tone'], string> = {
  green: 'bg-emerald-100/90 text-emerald-900',
  red: 'bg-red-100/90 text-red-900',
  yellow: 'bg-amber-100 text-amber-950',
}

function groupBySectionLabel(groups: SkillGapSectionGroup[]) {
  const order: string[] = []
  const map = new Map<string, SkillGapSectionGroup[]>()
  for (const group of groups) {
    if (!map.has(group.sectionLabel)) {
      map.set(group.sectionLabel, [])
      order.push(group.sectionLabel)
    }
    map.get(group.sectionLabel)!.push(group)
  }
  return order.map((label) => ({ sectionLabel: label, subgroups: map.get(label)! }))
}

export default function OptimizationSkillsGapPanel({
  summary,
  variant = 'section',
}: OptimizationSkillsGapPanelProps) {
  if (!summary) return null
  const compact = variant === 'modal'
  const { sectionGroups, greenCount, validated, fallbackMessage } = summary
  const hasActionItems = sectionGroups.some((g) => g.tone !== 'green')

  if (validated === false && fallbackMessage) {
    return (
      <div
        className={`${SECTION_FEEDBACK_CALLOUT_CLASS} text-amber-950 ${compact ? 'text-xs' : 'text-sm'}`}
        data-testid="skills-gap-recommendations"
        data-section-quality-feedback={compact ? undefined : 'skills'}
      >
        {compact ? (
          <>
            <p className="font-semibold text-amber-900">Skills gap</p>
            <p className="mt-1 text-amber-800/90">{fallbackMessage}</p>
          </>
        ) : (
          <>
            <p className={SECTION_FEEDBACK_TITLE_CLASS}>What to fix</p>
            <ul className={`mt-1.5 ${SECTION_FEEDBACK_LIST_CLASS}`}>
              <li className="text-xs sm:text-sm">{fallbackMessage}</li>
            </ul>
          </>
        )}
      </div>
    )
  }

  if (!hasActionItems) return null

  const sectionBlocks = groupBySectionLabel(sectionGroups)

  return (
    <div
      className={`${SECTION_FEEDBACK_CALLOUT_CLASS} text-amber-950 ${
        compact ? 'text-xs' : 'text-sm'
      }`}
      data-testid="skills-gap-recommendations"
      data-section-quality-feedback={compact ? undefined : 'skills'}
    >
      <p className={compact ? 'font-semibold text-amber-900' : SECTION_FEEDBACK_TITLE_CLASS}>
        {compact ? 'Skills gap' : 'What to fix'}
      </p>
      <p className="mt-1.5 text-amber-800/90 text-xs sm:text-sm">
        We never auto-add skills from the job description.
        {greenCount > 0 ? ` ${greenCount} already match on your resume.` : ''}
      </p>

      <div className={`mt-3 space-y-3 ${compact ? 'text-[11px]' : ''}`}>
        {sectionBlocks.map(({ sectionLabel, subgroups }) => (
          <section key={sectionLabel} data-testid={`skills-gap-section-${sectionLabel.toLowerCase().replace(/\s+/g, '-')}`}>
            <h3 className="text-sm font-semibold text-amber-950">{sectionLabel}</h3>
            <div className="mt-1.5 space-y-2">
              {subgroups.map((group) => (
                <div key={`${group.sectionLabel}-${group.subsectionLabel}-${group.tone}`}>
                  <p className="font-medium text-amber-900">{group.subsectionLabel}</p>
                  <p className="mt-0.5 text-amber-800/90">{group.hint}</p>
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {group.items.map((item, itemIndex) => (
                      <li
                        key={`${group.tone}-${itemIndex}-${item}`}
                        className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${TONE_CHIP_CLASS[group.tone]}`}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
